// src/components/Tenants/EditTenantDialog.js
// @flow

import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { log } from '../../utils/jsUtils';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { selectDefaultUnit } from '../../reduxStore/modules/user/selectors';
import { selectAllTags } from '../../reduxStore/modules/tags';

import { formatPhoneNumberIntl } from 'react-phone-number-input';
import * as R from 'ramda';

import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';

import AutocompleteProperty from '../Properties/AutocompleteProperty';
import TagRenderer from '../SharedComponents/TagRenderer';
import StyledPhoneInput from '../SharedComponents/StyledPhoneInput';

import addTenantDialogStyles from './Styles/AddTenantDialog.Style'; 
import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../utils/stylesUtils';

import type { Tenant } from '../../services/SPBackend';

type Props = {
  open: boolean,
  setOpen: (open: boolean) => void,
  currTenant: ?Tenant,
  triggerAction: (tenantData: any, moveTasks: boolean, cbSuccess: Function) => void
};

const EditTenantDialog = (props: Props) => {
  const { open, setOpen, currTenant, triggerAction } = props;
  // Replace legacy store with Redux hooks
  const i18n = useI18n();
  const defaultUnit = useSelector(selectDefaultUnit);
  const tags = useSelector(selectAllTags);
  const location = useLocation();
  const theme = useTheme();
  const classes = createClasses(addTenantDialogStyles, theme);

  // Local states
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState({
    _id: null,
    FirstName: '',
    LastName: '',
    Phone: '',
    Email: '',
    Keys: defaultUnit ? [defaultUnit.RoomKey] : '',
    Tags: []
  });
  const [error, setError] = useState('');
  const [moveTasks, setMoveTasks] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (currTenant && open) {
      setError('');
      setTenant({
        _id: currTenant._id,
        FirstName: currTenant.FirstName || '',
        LastName: currTenant.LastName || '',
        Phone: currTenant.Phone ? `+${currTenant.Phone}` : '',
        Email: currTenant.Email || '',
        Keys:
          defaultUnit
            ? [defaultUnit.RoomKey]
            : currTenant.unitsDocs && currTenant.unitsDocs.length > 0 
              ? [currTenant.unitsDocs[0].RoomKey]
              : '',
        Tags: [...(currTenant.Tags || [])]
      });
    }
  }, [currTenant, open, defaultUnit]);

  const handleCheckbox = (event) => {
    const isChecked = event.target.checked;
    log('[EditTenantDialog.handleCheckbox] isChecked:', isChecked);
    setChecked(isChecked);
    setMoveTasks(isChecked);
  };

  const callbackOnTagPicked = (params) => {
    log('[EditTenantDialog.callbackOnTagPicked] new tag:', params);
    if (!params || !params._id) return;
    
    setTenant((prev) => {
      // Ensure Tags is a mutable array and prevent duplicates
      const currentTags = prev.Tags || [];
      const tagId = params._id;
      
      // Check if tag already exists
      if (currentTags.includes(tagId)) {
        log('[EditTenantDialog.callbackOnTagPicked] Tag already exists:', tagId);
        return prev;
      }
      
      // Create new array with the added tag
      return {
        ...prev,
        Tags: [...currentTags, tagId]
      };
    });
  };
  
  const callbackOnTagRemoved = (params) => {
    log('[EditTenantDialog.callbackOnTagRemoved] removing tag:', params);
    if (!params || !params._id) return;
    
    setTenant((prev) => ({
      ...prev,
      Tags: (prev.Tags || []).filter(tagId => tagId !== params._id)
    }));
  };

  return (
    <Dialog
      aria-labelledby="form-add-unit"
      onClose={() => setOpen(false)}
      open={open}
    >
      <Box
        component="form"
        autoComplete="off"
        className={classNames('xlarge')}
        sx={classes.dialogFormCtr}
        // using 'xlarge' so the style can apply e.g. maxHeight 600
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setLoading(true);

          if (tenant.Keys !== null) {
            triggerAction(tenant, moveTasks, () => {
              setLoading(false);
              setOpen(false);
            });
          } else {
            setLoading(false);
            setError('Please select a unit from the dropdown');
          }
        }}
      >
        <DialogTitle id="form-dialog-title">
          {`Edit ${i18n.t('tenant')}`}
        </DialogTitle>

        <DialogContent
          sx={{ ...classes.grow, ...classes.flexContainer }}
        >
          <Box id="EditTenant">
            <Input
              autoFocus
              sx={classes.secondInput}
              fullWidth
              id="firstName"
              placeholder="First name"
              type="text"
              value={tenant.FirstName}
              onChange={(e) => {
                setTenant({ ...tenant, FirstName: e.target.value });
              }}
            />

            <Input
              sx={classes.secondInput}
              fullWidth
              id="lastName"
              placeholder="Last name"
              type="text"
              value={tenant.LastName}
              onChange={(e) => {
                setTenant({ ...tenant, LastName: e.target.value });
              }}
            />

            <Input
              sx={classes.secondInput}
              fullWidth
              id="email"
              placeholder="Email"
              type="email"
              value={tenant.Email}
              onChange={(e) => {
                setTenant({ ...tenant, Email: e.target.value });
              }}
            />

            <Box sx={{ marginTop: 2 }}>
              <StyledPhoneInput
                defaultCountry="US"
                placeholder="Enter phone number"
                value={formatPhoneNumberIntl(tenant.Phone)}
                onChange={(value) => {
                  setTenant({ ...tenant, Phone: value });
                }}
              />
            </Box>

            <TagRenderer
              tagsIds={tenant.Tags}
              customerId={tenant._id || 'temp'}
              readonly={false}
              callbackOnTagPicked={callbackOnTagPicked}
              callbackOnTagRemoved={callbackOnTagRemoved}
            />

            {/* 
              For selecting a new unit, show AutocompleteProperty + "move tasks" checkbox
              If you removed or disabled the code, you can re-add it here:
            */}
            {/* 
            <div sx={addTenantDialogStyles.secondInput(theme)}>
              <AutocompleteProperty
                inputTenant={currTenant}
                setUnit={(unit) => {
                  setTenant({
                    ...tenant,
                    Keys: unit ? [unit.RoomKey] : null
                  });
                }}
              />
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={handleCheckbox}
                    />
                  }
                  label="Move all tasks to the new unit"
                />
              </FormGroup>
            </div>
            */}
          </Box>

          <DialogContentText sx={classes.error}>
            {error}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            color="primary"
            onClick={(event) => {
              event.preventDefault();
              setOpen(false);
            }}
            type="button"
          >
            {'Cancel'}
          </Button>

          <Button
            color="primary"
            disabled={loading}
            type="submit"
            variant="contained"
          >
            {'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default EditTenantDialog;